/**
 * Device Status Utilities
 * 
 * Consistent device status logic used across all components:
 * - FamilyDashboard
 * - DevicesStatusCard
 * - DevicesManagementCard
 * - CallModal
 * - SystemSettings
 */

/**
 * Check if device is properly paired
 * @param {Object} device - Device object
 * @returns {boolean} True if device is paired with a senior
 */
export const isPaired = (device) => {
  return device?.status === 'paired' && Boolean(device?.senior_id);
};

/**
 * Check if device is online
 * Uses unified status logic matching backend DeviceStatusService
 * @param {Object} device - Device object
 * @param {number} maxHeartbeatAge - Max heartbeat age in milliseconds (default: 3 minutes)
 * @returns {boolean} True if device is currently online
 */
export const isOnline = (device, maxHeartbeatAge = 3 * 60 * 1000) => {
  if (!device) return false;
  
  // Check connection_state first
  if (device.connection_state === 'online') {
    console.debug('[deviceStatus] Online: connection_state=online');
    return true;
  }
  
  // Fallback: Check recent activity (last_active or last_heartbeat_at)
  const lastActivityTime = device.last_heartbeat_at || device.last_active;
  
  if (lastActivityTime) {
    try {
      const lastActivity = new Date(lastActivityTime).getTime();
      const now = Date.now();
      const age = now - lastActivity;
      
      // Consider online if activity is recent (< 3 minutes by default)
      if (age < maxHeartbeatAge) {
        console.debug(`[deviceStatus] Online: activity ${Math.floor(age/1000)}s ago`);
        return true;
      }
      
      console.debug(`[deviceStatus] Offline: activity ${Math.floor(age/1000)}s ago (> ${Math.floor(maxHeartbeatAge/1000)}s threshold)`);
    } catch (error) {
      console.warn('[deviceStatus] Error parsing activity timestamp:', error);
    }
  } else {
    console.debug('[deviceStatus] Offline: no activity data');
  }
  
  return false;
};

/**
 * Get device status using unified backend service
 * @param {Object} device - Device object
 * @returns {string} Device status: "online", "idle", or "offline"
 */
export const getDeviceStatus = (device) => {
  if (!device) return "offline";
  
  const online = isOnline(device);
  return online ? "online" : "offline";
};

/**
 * Check if device is ready for video calls
 * @param {Object} device - Device object
 * @returns {boolean} True if device can receive calls
 */
export const isCallReady = (device) => {
  return isPaired(device) && isOnline(device);
};

/**
 * Get device status display info
 * @param {Object} device - Device object
 * @returns {Object} { emoji, text, color, badge }
 */
export const getDeviceStatusDisplay = (device) => {
  const paired = isPaired(device);
  const online = isOnline(device);
  
  if (paired && online) {
    return {
      emoji: '🟢',
      text: 'Verbunden & Online',
      color: 'green',
      badge: 'bg-green-500/20 text-green-300 border-green-500/40'
    };
  }
  
  if (paired && !online) {
    return {
      emoji: '🔵',
      text: 'Gekoppelt, Offline',
      color: 'blue',
      badge: 'bg-blue-500/20 text-blue-300 border-blue-500/40'
    };
  }
  
  return {
    emoji: '⚪',
    text: 'Noch nicht gekoppelt',
    color: 'gray',
    badge: 'bg-neutral-500/20 text-neutral-300 border-neutral-500/40'
  };
};

/**
 * Get call button tooltip/disabled reason
 * @param {Object} device - Device object
 * @returns {string|null} Reason if disabled, null if enabled
 */
export const getCallDisabledReason = (device) => {
  if (!device) {
    return 'Kein Gerät ausgewählt';
  }
  
  const paired = isPaired(device);
  const online = isOnline(device);
  
  if (!paired) {
    return 'Gerät muss zuerst gekoppelt werden';
  }
  
  if (!online) {
    return 'Gerät ist aktuell offline';
  }
  
  return null; // Ready for calls
};

/**
 * Get device connection state description
 * @param {Object} device - Device object
 * @returns {string} Human-readable connection description
 */
export const getConnectionDescription = (device) => {
  if (!device) return 'Unbekannt';
  
  const paired = isPaired(device);
  const online = isOnline(device);
  
  if (paired && online) {
    return 'Das Gerät ist mit dem Internet verbunden und bereit für Anrufe.';
  }
  
  if (paired && !online) {
    return 'Das Gerät ist gekoppelt, aber aktuell nicht mit dem Internet verbunden.';
  }
  
  return 'Das Gerät wurde noch nicht mit einem Senioren gekoppelt.';
};

/**
 * Format last active time
 * @param {string|Date} lastActive - Last active timestamp
 * @returns {string} Human-readable time ago
 */
export const formatLastActive = (lastActive) => {
  if (!lastActive) return 'Nie';
  
  const now = new Date();
  const lastActiveDate = new Date(lastActive);
  const diffMs = now - lastActiveDate;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Gerade eben';
  if (diffMins < 60) return `vor ${diffMins} Min.`;
  if (diffHours < 24) return `vor ${diffHours} Std.`;
  if (diffDays < 7) return `vor ${diffDays} Tag${diffDays !== 1 ? 'en' : ''}`;
  
  return lastActiveDate.toLocaleDateString('de-DE');
};

/**
 * Get device icon based on type
 * @param {string} deviceType - Device type (tablet, phone, etc.)
 * @returns {string} Emoji icon
 */
export const getDeviceIcon = (deviceType) => {
  switch (deviceType?.toLowerCase()) {
    case 'tablet':
      return '📱';
    case 'phone':
      return '📞';
    case 'desktop':
      return '🖥️';
    default:
      return '📱';
  }
};

/**
 * Check if device is in an inconsistent state
 * Inconsistent = has senior_id or household_id but status !== 'paired'
 * @param {Object} device - Device object
 * @returns {boolean} True if device is in inconsistent state
 */
export const isInconsistent = (device) => {
  if (!device) return false;
  
  const paired = isPaired(device);
  const hasSeniorId = Boolean(device?.senior_id);
  const hasHouseholdId = Boolean(device?.household_id);
  
  // Inconsistent if NOT paired but has assignments
  return !paired && (hasSeniorId || hasHouseholdId);
};

/**
 * Normalize device status with inconsistency detection
 * @param {Object} device - Device object
 * @param {boolean} isInCall - Whether device is currently in a call
 * @returns {Object} Normalized status info
 */
export const normalizeDeviceStatus = (device, isInCall = false) => {
  if (!device) {
    return {
      isPaired: false,
      isOnline: false,
      isInconsistent: false,
      label: 'Unbekannt',
      emoji: '❓',
      badge: 'bg-neutral-500/20 text-neutral-300 border-neutral-500/40',
      priority: 5,
      statusType: 'unknown',
      tooltip: 'Gerätestatus unbekannt'
    };
  }
  
  const paired = isPaired(device);
  const online = isOnline(device);
  const inconsistent = isInconsistent(device);
  
  // Priority 1: Inconsistent State (highest priority for visibility)
  if (inconsistent) {
    return {
      isPaired: false,
      isOnline: false,
      isInconsistent: true,
      label: 'Inkonsistenter Zustand',
      emoji: '⚠️',
      badge: 'bg-orange-500/20 text-orange-300 border-orange-500/40',
      priority: 1,
      statusType: 'inconsistent',
      tooltip: 'Dieses Gerät ist einem Senior zugeordnet, aber nicht korrekt gekoppelt. Bitte bereinigen.'
    };
  }
  
  // Priority 2: Active call
  if (isInCall && paired && online) {
    return {
      isPaired: true,
      isOnline: true,
      isInconsistent: false,
      label: 'Im Anruf',
      emoji: '📞',
      badge: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/40',
      priority: 2,
      statusType: 'call',
      tooltip: 'Videoanruf läuft'
    };
  }
  
  // Priority 3: Paired & Online
  if (paired && online) {
    return {
      isPaired: true,
      isOnline: true,
      isInconsistent: false,
      label: 'Gekoppelt, Online',
      emoji: '🟢',
      badge: 'bg-green-500/20 text-green-300 border-green-500/40',
      priority: 3,
      statusType: 'online',
      tooltip: 'Gerät ist gekoppelt und online'
    };
  }
  
  // Priority 4: Paired but Offline
  if (paired && !online) {
    return {
      isPaired: true,
      isOnline: false,
      isInconsistent: false,
      label: 'Gekoppelt, Offline',
      emoji: '🔵',
      badge: 'bg-blue-500/20 text-blue-300 border-blue-500/40',
      priority: 4,
      statusType: 'offline',
      tooltip: 'Gerät ist gekoppelt, aber aktuell offline'
    };
  }
  
  // Priority 5: Not Paired (clean state)
  return {
    isPaired: false,
    isOnline: false,
    isInconsistent: false,
    label: 'Noch nicht gekoppelt',
    emoji: '⚪',
    badge: 'bg-neutral-500/20 text-neutral-300 border-neutral-500/40',
    priority: 5,
    statusType: 'unpaired',
    tooltip: 'Gerät wurde noch nicht mit einem Senior gekoppelt'
  };
};

/**
 * Get comprehensive device status for UI display (DEPRECATED - use normalizeDeviceStatus)
 * @deprecated Use normalizeDeviceStatus instead for better inconsistency handling
 */
export const getComprehensiveDeviceStatus = (device, isInCall = false) => {
  return normalizeDeviceStatus(device, isInCall);
};

/**
 * Format device UUID for display (first 8 characters)
 * @param {string} uuid - Device UUID
 * @returns {string} Formatted UUID
 */
export const formatDeviceUUID = (uuid) => {
  if (!uuid) return 'N/A';
  return uuid.slice(0, 8);
};

/**
 * Get household name for a device
 * @param {Object} device - Device object
 * @param {Array} households - List of households
 * @returns {string} Household name
 */
export const getDeviceHousehold = (device, households) => {
  if (!device?.household_id) return 'Unbekannt';
  const household = households?.find(h => h.id === device.household_id);
  return household?.name || 'Unbekannt';
};
